import React, { memo, useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import _ from "lodash";

interface ReportLeftProps {
  // isOpen?: boolean;
  pages: Array<{ id: number; page?: number; title: string; thumbnailUrl?: string | null }>;
  selectedPage: number;
  onPageSelect: (pageId: number) => void;
  reportAttachments?: any;
  isLogin?: boolean;
  reportType?: number;
  isPaymentEd?: boolean;
  currentPage?: number;
  isOpen?: boolean;
  aspectRatio: string | null;
  isMobile?: boolean;
  isJumpingRef?: React.RefObject<boolean>;
}

const ReportLeft = ({
  isMobile = false,
  isOpen = true,
  pages,
  isJumpingRef,
  selectedPage,
  onPageSelect,
  reportAttachments,
  isLogin = false,
  reportType = 1,
  isPaymentEd = false,
  currentPage,
  aspectRatio,
}: ReportLeftProps) => {
  const t = useTranslations("messages");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [imgLoaded, setImgLoaded] = useState<Record<number, boolean>>({});
  const scrollDebounceRef = useRef<number | null>(null);
  // ...existing code...
  useEffect(() => {
    if (!pages || !pages.length) return;

    const index = pages.findIndex((page) => page.id === currentPage);
    const container = scrollContainerRef.current;
    const thumbnail = thumbnailRefs.current[index];

    if (index === -1 || !thumbnail || !container) return;

    // Hủy debounce cũ nếu có
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = null;
    }

    // Giảm debounce xuống 50ms để phản hồi nhanh hơn, tránh giật khi bấm nhanh
    scrollDebounceRef.current = window.setTimeout(() => {
      requestAnimationFrame(() => {
        // Đợi DOM ready trước khi tính toán
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const thumbnailCenter = isMobile
          ? thumbnail.offsetLeft + thumbnail.clientWidth / 2
          : thumbnail.offsetTop + thumbnail.clientHeight / 2;
        const containerCenter = isMobile
          ? container.clientWidth / 2
          : container.clientHeight / 2;
        const newScroll = thumbnailCenter - containerCenter;

        // Giới hạn scroll trong khoảng hợp lệ
        const maxScroll = isMobile
          ? container.scrollWidth - container.clientWidth
          : container.scrollHeight - container.clientHeight;
        const clampedScroll = Math.max(0, Math.min(newScroll, maxScroll));

        // Scroll mượt với behavior: "smooth" để luôn ở giữa
        container.scrollTo({
          [isMobile ? "left" : "top"]: clampedScroll,
          behavior: "smooth",
        });
      });
    }, 50); // Giảm từ 120ms xuống 50ms

    return () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
    };
  }, [currentPage, pages, isMobile]);

  useEffect(() => {
    const handleContextMenu = (event: Event) => {
      event.preventDefault();
    };
    const images = scrollContainerRef.current?.querySelectorAll("img");
    images?.forEach((img) => {
      img.addEventListener("contextmenu", handleContextMenu);
      img.addEventListener("dragstart", handleContextMenu);
      img.style.userSelect = "none";
      img.style.pointerEvents = "auto";
    });
    return () => {
      images?.forEach((img) => {
        img.removeEventListener("contextmenu", handleContextMenu);
        img.removeEventListener("dragstart", handleContextMenu);
      });
    };
  }, [pages]);

  // if (!aspectRatio) {
  //   // Hoặc trả về một spinner/skeleton loading
  //   return <Box className="w-full md:w-[214px] h-auto md:h-[1300px] p-3 ...">Loading thumbnails...</Box>;
  // }
  return (
    <Box
      className={`w-full md:w-[214px] h-auto md:h-[1300px] p-3 md:p-[24px] gap-2 md:gap-[24px] thin-scrollbar`}
      sx={{
        borderRight: { md: "1px solid #E8E8E8" },
        borderBottom: { xs: "1px solid #E8E8E8", md: "none" },
        overflowY: "auto",
        overflowX: { xs: "auto", md: "hidden" },
      }}
      ref={!isMobile ? scrollContainerRef : undefined}
    >
      <Box
        className="flex md:flex-col gap-4 md:gap-[8px] w-full md:overflow-x-hidden overflow-x-auto pb-2 md:pb-0"
        ref={isMobile ? scrollContainerRef : undefined}
      >
        {pages?.map((page, index) => (
          <Tooltip
            key={page.id}
            translation-key="report_left_page_caption"
            title={t("report_left_page_caption", { number: page.id })}
            placement="top"
            followCursor={true}
            enterDelay={100}
            leaveDelay={0}
            sx={{
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#000000",
                color: "#ffffff",
                fontSize: "0.75rem",
                padding: "4px 8px",
                borderRadius: "2px",
                maxWidth: "none",
              },
            }}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <Card
              ref={(el: any) => thumbnailRefs.current[index] = el}
              onClick={(e) => {
                 e.stopPropagation();
                 if (isJumpingRef) isJumpingRef.current = true;
                 requestAnimationFrame(() => {
                   onPageSelect(page.id);
                 });
              }}
            className={`min-w-[100px] md:min-w-[160px] w-[160px] md:w-[160px] cursor-pointer
                ${selectedPage === page.id
                ? "border-1 border-blue-500"
                : "border-2 border-transparent"
              }
                  ${!imgLoaded[page.id] ? "opacity-50 pointer-events-none" : ""}
              `}
            elevation={0}
            sx={{
              borderRadius: "0px",

            }}
            >
            <CardMedia
              component="div"
              className="relative w-full bg-gray-100 overflow-hidden"
              sx={{
                border:
                  currentPage === page.id
                    ? "2px solid #3B82F6"
                    : "2px solid transparent",
                ...(isMobile && aspectRatio ? { aspectRatio } : {}),
              }}
            >
              <Box className="w-full h-full flex items-center justify-center text-gray-400">
                <Image
                  src={
                    page.thumbnailUrl || "images/default-page-thumbnail.png"
                  }
                  width={500}
                  height={600}
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                  priority={[0, 1, 2, 3, 4].includes(index)}
                  sizes="(max-width: 600px) 80vw, (max-width: 1200px) 60vw, 60vw"
                  decoding={index < 5 ? "sync" : "async"}
                  loading={index < 5 ? "eager" : "lazy"}
                  alt={t("report_left_page_thumbnail_alt", {
                    number: page.id,
                  })}
                  onLoad={() => setImgLoaded(prev => ({ ...prev, [page.id]: true }))}
                />
                {!imgLoaded[page.id] && (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    animation="wave"
                    sx={{ bgcolor: "#eee", position: "absolute", top: 0, left: 0 }}
                  />
                )}
              </Box>
            </CardMedia>
            <CardContent className="py-1 px-2">
              <Typography
                variant="body2"
                className="text-center !text-gray_90 !font-[Montserrat,sans-serif] !font-[500] !text-[10px] md:!text-[12px] !leading-[20px] md:!leading-[24px] !tracking-[0px]"
              >
                {t("bookmark_review_page_number", { number: page?.page || 0 })}
              </Typography>
            </CardContent>
          </Card>
          </Tooltip>
        ))}
      {pages?.length === 0 && (
        <Typography className="text-gray-500 italic p-4 text-center">
          {t("report_left_no_pages")}
        </Typography>
      )}
    </Box>
    </Box >
  );
};

export default memo(ReportLeft, (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
});
